import { DEFAULT_LOCALE, type Locale } from '@diana/shared'
import { plural } from '../copyShell.js'

/** The lesson player's chrome, in the student's language. Polish learning text never goes through here. */
export type PlayerStrings = {
  exit: string
  progress: string
  stepOf: (n: number, total: number) => string
  back: string
  next: string
  skip: string
  check: string
  start: string
  lesson: (n: number) => string
  exercise: string
  outline: string
  homework: string
  cover: { after: string; minutes: (label: string) => string }
  words: { words: string; phrases: string; hint: string; tapForExample: string; listen: string; tapToClose: string }
  explain: { showPl: string; hidePl: string }
  dialogue: { label: (n: number, total: number) => string; extra: string; close: string }
  listen: {
    scene: (n: number, total: number) => string
    play: string
    pause: string
    slow: string
    normal: string
    showText: string
    hideText: string
    deviceVoice: string
    deviceVoiceHint: string
    noVoice: string
    line: (n: number, total: number) => string
    toExercise: string
  }
  figure: { pending: (n: number) => string }
  ex: {
    allRight: string
    tryAgain: string
    fixWrong: (n: number) => string
    good: string
    correctIs: string
    almost: string
    letters: string
    writeHere: string
    showModel: string
    hideModel: string
    agrees: string
    needsWork: string
    needsWorkHint: string
    words: (n: number) => string
    saved: string
    showKey: string
    hideKey: string
    stale: string
    failed: string
    bank: string
    pick: string
    tapWords: string
    clear: string
    true: string
    false: string
  }
  finish: {
    great: string
    done: (lesson: number, right: number, total: number) => string
    doneNoScore: (lesson: number) => string
    homework: string
    homeworkMeta: (parts: number) => string
    startHomework: string
    fixWrong: (n: number) => string
    nextLesson: (n: number) => string
    backToCourse: string
  }
  error: { step: string; retry: string; skip: string }
}

const pl: PlayerStrings = {
  exit: 'Wyjdź z lekcji',
  progress: 'Postęp lekcji',
  stepOf: (n, t) => `krok ${n} z ${t}`,
  back: 'Wstecz',
  next: 'Dalej',
  skip: 'Pomiń',
  check: 'Sprawdź',
  start: 'Zaczynamy',
  lesson: (n) => `Lekcja ${n}`,
  exercise: 'Ćwiczenie',
  outline: 'Plan lekcji',
  homework: 'Praca domowa',
  cover: { after: 'Po lekcji umiesz', minutes: (l) => `≈ ${l.replace(/^ok\.\s*/, '')}` },
  words: {
    words: 'Słowa',
    phrases: 'Zwroty',
    hint: 'Dotknij karty — zobaczysz przykład.',
    tapForExample: 'Dotknij — zobaczysz przykład',
    listen: 'Posłuchaj',
    tapToClose: 'Dotknij, żeby schować przykład',
  },
  explain: { showPl: 'Pokaż po polsku', hidePl: 'Schowaj polski' },
  dialogue: { label: (n, t) => `Dialog ${n} z ${t}`, extra: 'Słowa dodatkowe', close: 'Zamknij' },
  listen: {
    scene: (n, t) => `Scena ${n} z ${t}`,
    play: 'Odtwórz',
    pause: 'Pauza',
    slow: 'Wolniej',
    normal: 'Normalnie',
    showText: 'Pokaż tekst nagrania',
    hideText: 'Schowaj tekst nagrania',
    deviceVoice: 'Czyta głos urządzenia',
    deviceVoiceHint: 'Nagranie jeszcze się przygotowuje — na razie tekst czyta polski głos Twojego urządzenia.',
    noVoice: 'Twoje urządzenie nie ma polskiego głosu. Przeczytaj tekst nagrania.',
    line: (n, t) => `${n} / ${t}`,
    toExercise: 'Do ćwiczenia',
  },
  figure: { pending: (n) => `Grafika ${n} — wkrótce` },
  ex: {
    allRight: '✓ Wszystko dobrze!',
    tryAgain: 'Popraw i sprawdź jeszcze raz',
    fixWrong: (n) => `Popraw błędne (${n})`,
    good: 'Dobrze!',
    correctIs: 'Poprawnie:',
    almost: 'Prawie! Sprawdź polskie litery:',
    letters: 'Polskie litery',
    writeHere: 'Napisz tutaj…',
    showModel: 'Pokaż wzór',
    hideModel: 'Schowaj wzór',
    agrees: 'Zgadza się',
    needsWork: 'Do poprawy',
    needsWorkHint: 'Popraw tekst i porównaj jeszcze raz.',
    words: (n) => `${n} ${plural(n, 'słowo', 'słowa', 'słów')}`,
    saved: 'Zapisane',
    showKey: 'Pokaż klucz',
    hideKey: 'Schowaj klucz',
    stale: 'To ćwiczenie zmieniło się od Twojej ostatniej próby — zrób je jeszcze raz.',
    failed: 'Nie udało się sprawdzić. Spróbuj jeszcze raz.',
    bank: 'Słowa do wyboru',
    pick: 'Wybierz',
    tapWords: 'Dotykaj słów po kolei',
    clear: 'Wyczyść',
    true: 'prawda',
    false: 'fałsz',
  },
  finish: {
    great: 'Świetnie!',
    done: (l, r, t) => `Lekcja ${l} ukończona · ${r} z ${t} dobrze`,
    doneNoScore: (l) => `Lekcja ${l} ukończona`,
    homework: 'Praca domowa',
    homeworkMeta: (p) => `${p} ${plural(p, 'zadanie', 'zadania', 'zadań')} · ok. 15 min · bez terminu`,
    startHomework: 'Zacznij pracę domową',
    fixWrong: (n) => `Popraw błędne (${n})`,
    nextLesson: (n) => `Lekcja ${n}`,
    backToCourse: 'Wróć do kursu',
  },
  error: { step: 'Nie udało się wczytać tego kroku.', retry: 'Spróbuj jeszcze raz', skip: 'Pomiń' },
}

const uk: PlayerStrings = {
  exit: 'Вийти з уроку',
  progress: 'Прогрес уроку',
  stepOf: (n, t) => `крок ${n} з ${t}`,
  back: 'Назад',
  next: 'Далі',
  skip: 'Пропустити',
  check: 'Перевірити',
  start: 'Почнімо',
  lesson: (n) => `Урок ${n}`,
  exercise: 'Вправа',
  outline: 'План уроку',
  homework: 'Домашнє завдання',
  cover: { after: 'Після уроку ти зможеш', minutes: (l) => `≈ ${l.replace(/^ok\.\s*/, '').replace('min', 'хв')}` },
  words: {
    words: 'Слова',
    phrases: 'Вислови',
    hint: 'Торкніться картки — побачите приклад.',
    tapForExample: 'Торкнись — побачиш приклад',
    listen: 'Послухати',
    tapToClose: 'Торкнись, щоб сховати приклад',
  },
  explain: { showPl: 'Показати польською', hidePl: 'Сховати польське' },
  dialogue: { label: (n, t) => `Діалог ${n} з ${t}`, extra: 'Додаткові слова', close: 'Закрити' },
  listen: {
    scene: (n, t) => `Сцена ${n} з ${t}`,
    play: 'Відтворити',
    pause: 'Пауза',
    slow: 'Повільно',
    normal: 'Звичайно',
    showText: 'Показати текст запису',
    hideText: 'Сховати текст запису',
    deviceVoice: 'Читає голос пристрою',
    deviceVoiceHint: 'Запис ще готується — поки що текст читає польський голос твого пристрою.',
    noVoice: 'На твоєму пристрої немає польського голосу. Прочитай текст запису.',
    line: (n, t) => `${n} / ${t}`,
    toExercise: 'До вправи',
  },
  figure: { pending: (n) => `Ілюстрація ${n} — незабаром` },
  ex: {
    allRight: '✓ Усе правильно!',
    tryAgain: 'Виправ і перевір ще раз',
    fixWrong: (n) => `Виправити помилки (${n})`,
    good: 'Добре!',
    correctIs: 'Правильно:',
    almost: 'Майже! Перевір польські літери:',
    letters: 'Польські літери',
    writeHere: 'Напиши тут…',
    showModel: 'Показати зразок',
    hideModel: 'Сховати зразок',
    agrees: 'Збігається',
    needsWork: 'Треба виправити',
    needsWorkHint: 'Виправ текст і порівняй ще раз.',
    words: (n) => `${n} ${plural(n, 'слово', 'слова', 'слів')}`,
    saved: 'Збережено',
    showKey: 'Показати ключ',
    hideKey: 'Сховати ключ',
    stale: 'Ця вправа змінилася від твоєї останньої спроби — зроби її ще раз.',
    failed: 'Не вдалося перевірити. Спробуй ще раз.',
    bank: 'Слова на вибір',
    pick: 'Вибери',
    tapWords: 'Торкайся слів по черзі',
    clear: 'Очистити',
    true: 'prawda',
    false: 'fałsz',
  },
  finish: {
    great: 'Świetnie!',
    done: (l, r, t) => `Урок ${l} пройдено · ${r} з ${t} правильно`,
    doneNoScore: (l) => `Урок ${l} пройдено`,
    homework: 'Praca domowa',
    homeworkMeta: (p) => `${p} ${plural(p, 'завдання', 'завдання', 'завдань')} · ≈ 15 хв · без дедлайну`,
    startHomework: 'Почати домашнє',
    fixWrong: (n) => `Виправити помилки (${n})`,
    nextLesson: (n) => `Lekcja ${n}`,
    backToCourse: 'Назад до курсу',
  },
  error: { step: 'Не вдалося завантажити цей крок.', retry: 'Спробувати ще', skip: 'Пропустити' },
}

const ru: PlayerStrings = {
  exit: 'Выйти из урока',
  progress: 'Прогресс урока',
  stepOf: (n, t) => `шаг ${n} из ${t}`,
  back: 'Назад',
  next: 'Дальше',
  skip: 'Пропустить',
  check: 'Проверить',
  start: 'Начнём',
  lesson: (n) => `Урок ${n}`,
  exercise: 'Упражнение',
  outline: 'План урока',
  homework: 'Домашнее задание',
  cover: { after: 'После урока ты сможешь', minutes: (l) => `≈ ${l.replace(/^ok\.\s*/, '').replace('min', 'мин')}` },
  words: {
    words: 'Слова',
    phrases: 'Выражения',
    hint: 'Нажмите на карточку — увидите пример.',
    tapForExample: 'Нажми — увидишь пример',
    listen: 'Послушать',
    tapToClose: 'Нажми, чтобы скрыть пример',
  },
  explain: { showPl: 'Показать по-польски', hidePl: 'Скрыть польский' },
  dialogue: { label: (n, t) => `Диалог ${n} из ${t}`, extra: 'Дополнительные слова', close: 'Закрыть' },
  listen: {
    scene: (n, t) => `Сцена ${n} из ${t}`,
    play: 'Воспроизвести',
    pause: 'Пауза',
    slow: 'Медленно',
    normal: 'Обычно',
    showText: 'Показать текст записи',
    hideText: 'Скрыть текст записи',
    deviceVoice: 'Читает голос устройства',
    deviceVoiceHint: 'Запись ещё готовится — пока текст читает польский голос твоего устройства.',
    noVoice: 'На твоём устройстве нет польского голоса. Прочитай текст записи.',
    line: (n, t) => `${n} / ${t}`,
    toExercise: 'К упражнению',
  },
  figure: { pending: (n) => `Иллюстрация ${n} — скоро` },
  ex: {
    allRight: '✓ Всё правильно!',
    tryAgain: 'Исправь и проверь ещё раз',
    fixWrong: (n) => `Исправить ошибки (${n})`,
    good: 'Хорошо!',
    correctIs: 'Правильно:',
    almost: 'Почти! Проверь польские буквы:',
    letters: 'Польские буквы',
    writeHere: 'Напиши здесь…',
    showModel: 'Показать образец',
    hideModel: 'Скрыть образец',
    agrees: 'Совпадает',
    needsWork: 'Нужно исправить',
    needsWorkHint: 'Исправь текст и сравни ещё раз.',
    words: (n) => `${n} ${plural(n, 'слово', 'слова', 'слов')}`,
    saved: 'Сохранено',
    showKey: 'Показать ключ',
    hideKey: 'Скрыть ключ',
    stale: 'Это упражнение изменилось после твоей последней попытки — сделай его ещё раз.',
    failed: 'Не удалось проверить. Попробуй ещё раз.',
    bank: 'Слова на выбор',
    pick: 'Выбери',
    tapWords: 'Нажимай на слова по порядку',
    clear: 'Очистить',
    true: 'prawda',
    false: 'fałsz',
  },
  finish: {
    great: 'Świetnie!',
    done: (l, r, t) => `Урок ${l} пройден · ${r} из ${t} правильно`,
    doneNoScore: (l) => `Урок ${l} пройден`,
    homework: 'Praca domowa',
    homeworkMeta: (p) => `${p} ${plural(p, 'задание', 'задания', 'заданий')} · ≈ 15 мин · без дедлайна`,
    startHomework: 'Начать домашнее',
    fixWrong: (n) => `Исправить ошибки (${n})`,
    nextLesson: (n) => `Lekcja ${n}`,
    backToCourse: 'Назад к курсу',
  },
  error: { step: 'Не удалось загрузить этот шаг.', retry: 'Попробовать ещё', skip: 'Пропустить' },
}

const all: Record<Locale, PlayerStrings> = { pl, uk, ru }

export function playerCopy(locale: Locale | undefined): PlayerStrings {
  return all[locale ?? DEFAULT_LOCALE] ?? all[DEFAULT_LOCALE]
}
